import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Grid, Switch, FormControlLabel, 
  Button, Divider, Alert, CircularProgress, Card, CardContent,
  Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import SyncIcon from '@mui/icons-material/Sync';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

// Tip management feature definitions
// Keys match the featurePricing keys used in Features & Limits
const tipFeatures = {
  tipManagement: {
    name: 'Tip Management System',
    description: 'Enable overall tip management including tip policies and role buckets',
    icon: <MonetizationOnIcon />,
    color: '#4CAF50'
  },
  shiftManagement: {
    name: 'Shift Management',
    description: 'Allow creating and managing work shifts with employee assignments',
    icon: <AccessTimeIcon />,
    color: '#2196F3'
  },
  shiftCloseout: {
    name: 'Shift Closeout',
    description: 'Enable shift closeout functionality for finalizing tips and generating reports',
    icon: <AssignmentTurnedInIcon />,
    color: '#9C27B0'
  },
  tipDistribution: {
    name: 'Tip Distribution',
    description: 'Allow tip distribution modes (direct tips vs pooled tips) configuration',
    icon: <AccountBalanceWalletIcon />,
    color: '#FF9800'
  },
  postCloseAdjustments: {
    name: 'Post-Close Adjustments',
    description: 'Allow post-closeout adjustments for refunds and voids (Enterprise only)',
    icon: <ReceiptLongIcon />,
    color: '#F44336'
  }
};

const tiers = ['free', 'standard', 'professional', 'enterprise'];

const emptyTierConfig = () => Object.fromEntries(tiers.map(tier => [tier, {}]));

const tierColors = {
  free: 'default',
  standard: 'info',
  professional: 'primary',
  enterprise: 'secondary'
};

const tierLabels = {
  free: 'Free',
  standard: 'Standard',
  professional: 'Professional',
  enterprise: 'Enterprise'
};

export default function TipManagementContent() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Store the full featurePricing config to preserve other features when saving
  const [fullFeaturePricing, setFullFeaturePricing] = useState({});
  const [tierPricing, setTierPricing] = useState({});
  
  // Local state for tip management features display (derived from featurePricing)
  const [tipConfig, setTipConfig] = useState({
    globalEnabled: true,
    tierConfig: emptyTierConfig()
  });

  // Convert featurePricing format to tip management display format
  const convertToTipConfig = useCallback((featurePricing) => {
    const config = {
      globalEnabled: true, // Will be set based on stored value or derived
      tierConfig: emptyTierConfig()
    };

    // Check the explicit global enabled flag first
    // This flag is stored in featurePricing._tipManagementGlobalEnabled
    if (featurePricing._tipManagementGlobalEnabled !== undefined) {
      config.globalEnabled = featurePricing._tipManagementGlobalEnabled;
    } else {
      // Fallback: Check if any tip feature is globally enabled
      let anyEnabled = false;
      Object.keys(tipFeatures).forEach(featureKey => {
        const feature = featurePricing[featureKey];
        if (feature?.enabled) {
          anyEnabled = true;
        }
      });
      config.globalEnabled = anyEnabled;
    }
    
    Object.keys(tipFeatures).forEach(featureKey => {
      const feature = featurePricing[featureKey];
      
      tiers.forEach(tier => {
        config.tierConfig[tier][featureKey] =
          Boolean(feature?.enabled && feature?.availableInTiers?.includes(tier));
      });
    });

    return config;
  }, []);

  // Convert tip management display format back to featurePricing format
  const convertToFeaturePricing = (tipConfig, existingPricing) => {
    const updatedPricing = { ...existingPricing };

    // Store the global enabled flag explicitly
    // This ensures sync between Tip Management tab and Features & Limits tab
    updatedPricing._tipManagementGlobalEnabled = tipConfig.globalEnabled;

    Object.keys(tipFeatures).forEach(featureKey => {
      // Get which tiers have this feature enabled
      const enabledTiers = tiers.filter(tier => 
        tipConfig.globalEnabled && tipConfig.tierConfig[tier]?.[featureKey]
      );

      // Determine if feature is enabled at all
      const isEnabled = tipConfig.globalEnabled && enabledTiers.length > 0;

      updatedPricing[featureKey] = {
        enabled: isEnabled,
        price: existingPricing[featureKey]?.price || 0,
        availableInTiers: enabledTiers
      };
    });

    return updatedPricing;
  };

  const fetchConfiguration = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch from the same endpoint as Features & Limits
      const response = await fetch('/api/config/feature-pricing');
      const data = await response.json();
      
      if (data.success) {
        setFullFeaturePricing(data.featurePricing || {});
        setTierPricing(data.tierPricing || {});
        
        // Convert to tip config display format
        const converted = convertToTipConfig(data.featurePricing || {});
        setTipConfig(converted);
      }
    } catch (err) {
      setError('Failed to load tip management configuration: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [convertToTipConfig]);

  useEffect(() => {
    fetchConfiguration();
  }, [fetchConfiguration]);

  const handleGlobalToggle = (enabled) => {
    setTipConfig(prev => ({
      ...prev,
      globalEnabled: enabled
    }));
  };

  const handleFeatureToggle = (tier, feature, enabled) => {
    setTipConfig(prev => ({
      ...prev,
      tierConfig: {
        ...prev.tierConfig,
        [tier]: {
          ...(prev.tierConfig[tier] || {}),
          [feature]: enabled
        }
      }
    }));
  };

  const handleEnableAllForTier = (tier) => {
    setTipConfig(prev => ({
      ...prev,
      tierConfig: {
        ...prev.tierConfig,
        [tier]: Object.keys(tipFeatures).reduce((acc, feature) => {
          acc[feature] = true;
          return acc;
        }, {})
      }
    }));
  };

  const handleDisableAllForTier = (tier) => {
    setTipConfig(prev => ({
      ...prev,
      tierConfig: {
        ...prev.tierConfig,
        [tier]: Object.keys(tipFeatures).reduce((acc, feature) => {
          acc[feature] = false;
          return acc;
        }, {})
      }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      // Convert tip config back to featurePricing format
      const updatedFeaturePricing = convertToFeaturePricing(tipConfig, fullFeaturePricing);
      
      // Save to the same endpoint as Features & Limits
      const response = await fetch('/api/config/feature-pricing', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          featurePricing: updatedFeaturePricing,
          tierPricing: tierPricing
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setFullFeaturePricing(updatedFeaturePricing);
        setSuccess('Tip management configuration saved! This is synced with Features & Limits. Changes take effect immediately for new app sessions.');
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError(data.error || 'Failed to save configuration');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const getEnabledCountForTier = (tier) => {
    const tierFeatures = tipConfig.tierConfig?.[tier] || {};
    return Object.values(tierFeatures).filter(Boolean).length;
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', p: 4 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading tip management configuration...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <strong>💰 Tip Management Feature Controls</strong> - Configure which tip management features 
        are available for each subscription tier. 
        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
          <SyncIcon sx={{ fontSize: 16 }} />
          <strong>Synced with Features & Limits</strong> - Changes here automatically update Features & Limits and vice versa.
        </Box>
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Global Control */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Global Tip Management Control
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Master switch to enable or disable the entire tip management system across all tiers.
              When disabled, no tip management features will be available regardless of tier settings.
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={tipConfig.globalEnabled}
                onChange={(e) => handleGlobalToggle(e.target.checked)}
                color="success"
                size="large"
              />
            }
            label={tipConfig.globalEnabled ? 'ENABLED' : 'DISABLED'}
            labelPlacement="start"
          />
        </Box>
      </Paper>

      {/* Feature Descriptions */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Feature Reference
        </Typography>
        <Grid container spacing={2}>
          {Object.entries(tipFeatures).map(([key, feature]) => (
            <Grid item xs={12} md={6} lg={4} key={key}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box sx={{ 
                    color: feature.color, 
                    mt: 0.5,
                    '& svg': { fontSize: 28 }
                  }}>
                    {feature.icon}
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {feature.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Tier Configuration Matrix */}
      <Paper sx={{ p: 3, mb: 3, opacity: tipConfig.globalEnabled ? 1 : 0.6 }}>
        <Typography variant="h6" gutterBottom>
          Tier-Based Feature Configuration
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Configure which tip management features are available for each subscription tier.
          {!tipConfig.globalEnabled && (
            <strong> (Disabled - Enable global tip management to configure tiers)</strong>
          )}
        </Typography>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Feature</TableCell>
                {tiers.map(tier => (
                  <TableCell key={tier} align="center" sx={{ fontWeight: 'bold' }}>
                    <Chip 
                      label={tierLabels[tier]} 
                      color={tierColors[tier]}
                      size="small"
                      sx={{ mb: 1 }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1 }}>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        onClick={() => handleEnableAllForTier(tier)}
                        disabled={!tipConfig.globalEnabled}
                      >
                        All On
                      </Button>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        color="inherit"
                        onClick={() => handleDisableAllForTier(tier)}
                        disabled={!tipConfig.globalEnabled}
                      >
                        All Off
                      </Button>
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(tipFeatures).map(([featureKey, feature]) => (
                <TableRow key={featureKey} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ color: feature.color }}>
                        {feature.icon}
                      </Box>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {feature.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {feature.description}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  {tiers.map(tier => (
                    <TableCell key={tier} align="center">
                      <Switch
                        checked={tipConfig.tierConfig?.[tier]?.[featureKey] || false}
                        onChange={(e) => handleFeatureToggle(tier, featureKey, e.target.checked)}
                        disabled={!tipConfig.globalEnabled}
                        color={tierColors[tier] === 'default' ? 'default' : tierColors[tier]}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Summary Row */}
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-around', pt: 1 }}>
          {tiers.map(tier => {
            const enabledCount = getEnabledCountForTier(tier);
            const totalCount = Object.keys(tipFeatures).length;
            return (
              <Box key={tier} sx={{ textAlign: 'center' }}>
                <Chip 
                  label={`${enabledCount}/${totalCount} enabled`}
                  color={enabledCount === totalCount ? 'success' : enabledCount > 0 ? 'warning' : 'default'}
                  size="small"
                />
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                  {tierLabels[tier]} Tier
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Paper>

      {/* Recommended Settings */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'action.hover' }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          💡 Recommended Configuration
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              <strong>Free Tier:</strong> All tip features disabled. Basic businesses 
              can use manual tip tracking.
            </Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              <strong>Standard Tier:</strong> Core tip, shift, closeout, and distribution 
              features. Post-close adjustments stay off.
            </Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              <strong>Professional Tier:</strong> Full tip management except post-close 
              adjustments. Perfect for restaurants and salons.
            </Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              <strong>Enterprise Tier:</strong> All features enabled including post-close 
              adjustments for complex multi-location operations.
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button 
          variant="outlined" 
          startIcon={<RestoreIcon />} 
          onClick={fetchConfiguration}
          disabled={saving}
        >
          Reset
        </Button>
        <Button 
          variant="contained" 
          startIcon={<SaveIcon />} 
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </Button>
      </Box>
    </Box>
  );
}
